import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Loader2, AlertTriangle, Trash2, Info, Lock } from 'lucide-react';
import { Button } from '../ui-v2';
import { Modal, Input } from '../ui-v2.5';
import { getBorradoAlta, borrarAltaEntera } from './api';

/**
 * BorrarAlta — el final de un alta que se cancela (pieza 5.4). Va debajo de «Deshacer el alta».
 *
 * Lo que sabe lo dice el SERVIDOR (`GET /altas/:id/borrado`): si se puede, qué lo impide y qué se
 * borraría. La pantalla no tiene su propia copia de las reglas.
 *
 *   · Mientras algo lo impida, dice QUÉ: «quedan 2 tareas», una contradicción con Inventario, o una
 *     referencia que el borrado no sabe tratar, con su nombre.
 *   · Con todo listo, una franja roja: ya se puede borrar, y es irreversible. El botón, sólo para
 *     quien tiene `incorporaciones.borrar_alta`; los demás leen quién puede.
 *
 * LA CONFIRMACIÓN TIENE TRES PARTES (decidido el 16/10), y las tres hacen falta:
 *   1. lo que se borra, NOMBRADO y con cifras;
 *   2. una casilla por cada tarea que nadie puede comprobar desde la intranet;
 *   3. teclear el número del alta. Es lo que distingue un borrado irreversible de un clic.
 *
 * `version` cambia cada vez que se marca una tarea de deshacer: es lo que hace volver a preguntar.
 */

const ACCION = { borrar: 'se borra', anonimizar: 'se quita el nombre' };

export default function BorrarAlta({ altaId, puedeBorrar, version, onBorrada }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState(false);
  const [abierto, setAbierto] = useState(false);
  const [marcadas, setMarcadas] = useState(() => new Set());
  const [numero, setNumero] = useState('');
  const [borrando, setBorrando] = useState(false);

  const cargar = useCallback(async () => {
    setError(false);
    try { setD(await getBorradoAlta(altaId)); } catch (_) { setError(true); }
  }, [altaId]);
  useEffect(() => { cargar(); }, [cargar, version]);

  const abrir = () => { setMarcadas(new Set()); setNumero(''); setAbierto(true); };

  async function borrar() {
    setBorrando(true);
    try {
      const r = await borrarAltaEntera(altaId, numero.trim(), [...marcadas]);
      setAbierto(false);
      toast.success(`Alta ${r.numero} borrada. No queda rastro de ella ni de su persona.`);
      const f = r.ficheros || {};
      if (f.fallidos && f.fallidos.length) {
        // La base ya está bien; lo que queda son ficheros sueltos en Azure, y se dicen.
        toast.warning(`${f.fallidos.length} fichero(s) no se pudieron borrar de Azure (${f.fallidos.map((x) => x.que).join(', ')}). `
          + 'Hay que borrarlos a mano: están en el registro del servidor.', { autoClose: false });
      }
      if (onBorrada) onBorrada(r);
    } catch (e) {
      const b = (e && e.response && e.response.data) || {};
      toast.error(b.message || 'No se pudo borrar el alta.');
      setAbierto(false);
      cargar();
    } finally {
      setBorrando(false);
    }
  }

  if (error) {
    return (
      <section className="rounded-v25-lg border border-v25-line bg-white px-4 py-3 text-sm text-red-600">
        No se pudo comprobar si el alta se puede borrar. <button type="button" className="underline" onClick={cargar}>Reintentar</button>
      </section>
    );
  }
  if (!d) {
    return (
      <section className="flex items-center gap-2 rounded-v25-lg border border-v25-line bg-white px-4 py-3 text-sm text-v25-ink-500">
        <Loader2 className="h-4 w-4 animate-spin" />Comprobando si se puede borrar…
      </section>
    );
  }

  // ── todavía no ──
  if (!d.puede) {
    return (
      <section className="rounded-v25-lg border border-v25-line bg-white px-4 py-3">
        <div className="flex items-center gap-2 text-sm font-medium text-v25-ink-700">
          <Lock className="h-4 w-4 text-v25-ink-400" />Todavía no se puede borrar
        </div>
        <ul className="mt-1.5 space-y-1">
          {d.motivos.map((m) => (
            <li key={m.clave} className="flex items-start gap-1.5 text-xs text-v25-ink-700">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-v25-full bg-v25-ink-400" />{m.texto}
            </li>
          ))}
        </ul>
      </section>
    );
  }

  // ── ya se puede ──
  const sb = d.se_borra;
  const pierdeAuditoria = (sb.sin_clave || []).find((x) => x.tabla === 'seguridad_consultas_secretos');
  const todasMarcadas = d.hay_que_confirmar.every((x) => marcadas.has(x.id));
  const numeroBien = numero.trim() === String(d.alta.numero);
  const cambiar = (id) => setMarcadas((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <>
      <section className="rounded-v25-lg border border-red-300 bg-red-50 px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-sm font-semibold text-red-800">
              <AlertTriangle className="h-4 w-4" />Ya se puede borrar
            </div>
            <p className="mt-0.5 text-xs text-red-800">
              {d.nada_que_deshacer ? 'Este alta no hizo nada que haya que deshacer.' : 'Todo lo que hizo esta alta está deshecho.'}
              {' '}Ya se puede borrar el alta y los datos de la persona. <strong>Es irreversible:</strong> no queda rastro.
            </p>
          </div>
          {puedeBorrar ? (
            <Button variant="destructive" onClick={abrir}><Trash2 />Borrar el alta…</Button>
          ) : (
            <span className="text-xs text-red-800">Lo puede borrar Recursos Humanos.</span>
          )}
        </div>
      </section>

      {abierto && (
        <Modal
          open
          onClose={() => !borrando && setAbierto(false)}
          size="md"
          tone="danger"
          title="Borrar el alta y los datos de la persona"
          footer={(
            <>
              <Button variant="outline" onClick={() => setAbierto(false)} disabled={borrando}>Cancelar</Button>
              <Button variant="destructive" onClick={borrar} disabled={borrando || !todasMarcadas || !numeroBien}>
                {borrando ? <Loader2 className="animate-spin" /> : <Trash2 />}Borrar para siempre
              </Button>
            </>
          )}
        >
          <div className="space-y-4 text-sm text-v25-ink-700">
            {/* 1 · lo que se borra, nombrado */}
            <div>
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-v25-ink-500">Lo que se borra</div>
              <ul className="space-y-1 rounded-v25-md border border-v25-line bg-v25-surface-sunken p-3 text-xs">
                <li><strong className="text-v25-ink-900">El alta {sb.alta.numero}</strong>: {sb.alta.tareas} tareas, {sb.alta.material} de material, {sb.alta.tareas_deshacer} de deshacer y {sb.alta.avisos} avisos.</li>
                {sb.persona && <li><strong className="text-v25-ink-900">{sb.persona.nombre}</strong> ({sb.persona.ambito}), con su ficha.</li>}
                {sb.solicitudes > 0 && <li>La solicitud de datos del candidato y lo que envió.</li>}
                {(sb.con_la_persona || []).map((x) => <li key={`p-${x.tabla}`}>{x.rotulo}: {x.filas}</li>)}
                {(sb.sin_clave || []).map((x) => <li key={`s-${x.tabla}`}>{x.rotulo}: {x.filas} · {ACCION[x.accion] || x.accion}</li>)}
                {sb.ficheros > 0 && <li>{sb.ficheros} fichero(s) en Azure: foto, documentos de identidad, titulaciones.</li>}
              </ul>
              {pierdeAuditoria && (
                <p className="mt-1.5 flex items-start gap-1.5 text-xs text-red-700">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  Se borra también el registro de quién consultó sus contraseñas. Después no se podrá saber quién las vio.
                </p>
              )}
            </div>

            {/* 2 · una casilla por cada tarea que nadie puede comprobar */}
            {d.hay_que_confirmar.length > 0 && (
              <div>
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-v25-ink-500">Confirma lo que la intranet no puede comprobar</div>
                <ul className="space-y-1.5">
                  {d.hay_que_confirmar.map((x) => (
                    <li key={x.id}>
                      <label className="flex cursor-pointer items-start gap-2 text-xs">
                        <input type="checkbox" className="mt-0.5" checked={marcadas.has(x.id)} onChange={() => cambiar(x.id)} disabled={borrando} />
                        <span className="min-w-0">
                          <span className="text-v25-ink-900">Está hecho: {x.descripcion}</span>
                          <span className="block text-[11px] text-v25-ink-500">{x.porque}</span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* 3 · el número, tecleado */}
            <div>
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-v25-ink-500">
                Para borrar, escribe el número del alta: <span className="font-mono normal-case text-v25-ink-900">{d.alta.numero}</span>
              </div>
              <Input value={numero} onChange={(e) => setNumero(e.target.value)} placeholder={d.alta.numero}
                autoComplete="off" aria-label="Número del alta" disabled={borrando} />
            </div>

            <p className="flex items-start gap-1.5 text-xs text-v25-ink-500">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              No hay forma de recuperarlo: ni el alta, ni la persona, ni sus ficheros.
            </p>
          </div>
        </Modal>
      )}
    </>
  );
}
